import { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItemButton, ListItemIcon, ListItemText, ListSubheader,
  FormControl, InputLabel, Select, MenuItem, Stack, TextField, InputAdornment,
  Tooltip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import Iconify from '../../../components/iconify';
import { getWidgetTypesByCategory } from './widgetRegistry';
import { friendlyNameConverter } from '../../../services/localizationService';

export default function WidgetPicker({ open, onClose, onAdd, recordings }) {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedRecordingId, setSelectedRecordingId] = useState('');
  const [selectedSensorKey, setSelectedSensorKey] = useState('');
  const [search, setSearch] = useState('');

  const categories = getWidgetTypesByCategory();

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        widgets: cat.widgets.filter((w) => w.label.toLowerCase().includes(q) || w.category.toLowerCase().includes(q)),
      }))
      .filter((cat) => cat.widgets.length > 0);
  }, [categories, search]);

  const selectedDef = useMemo(() => {
    for (const cat of categories) {
      const found = cat.widgets.find((w) => w.type === selectedType);
      if (found) return found;
    }
    return null;
  }, [categories, selectedType]);

  const selectedRecording = recordings.find((r) => r.id === selectedRecordingId);
  const sensorKeys = selectedRecording ? Object.keys(selectedRecording.data || {}) : [];

  const handleAdd = () => {
    if (!selectedType || !selectedDef) return;
    if (selectedDef.requiresRecording && !selectedRecordingId) return;
    onAdd({
      id: uuidv4(),
      type: selectedType,
      recordingId: selectedDef.requiresRecording ? selectedRecordingId : null,
      sensorKey: selectedDef.requiresSensor ? selectedSensorKey : null,
      position: { x: 0, y: 0, w: selectedDef.defaultSize.w, h: selectedDef.defaultSize.h },
    });
    setSelectedType(null);
    setSelectedRecordingId('');
    setSelectedSensorKey('');
    setSearch('');
    onClose();
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Panel</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            placeholder="Search panels..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={20} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />

          <List sx={{ maxHeight: 320, overflow: 'auto' }} dense>
            {filteredCategories.map((cat) => (
              <li key={cat.category}>
                <ul style={{ padding: 0 }}>
                  <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '32px', fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>
                    {cat.category}
                  </ListSubheader>
                  {cat.widgets.map((wt) => {
                    const available = !wt.canUse || wt.canUse(recordings);
                    const button = (
                      <ListItemButton
                        key={wt.type}
                        selected={selectedType === wt.type}
                        disabled={!available}
                        onClick={() => available && setSelectedType(wt.type)}
                        sx={{ py: 0.5, ...(available ? {} : { opacity: 0.45 }) }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Iconify icon={wt.icon} width={20} />
                        </ListItemIcon>
                        <ListItemText primary={wt.label} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItemButton>
                    );
                    return available ? button : (
                      <Tooltip key={wt.type} title="No compatible data in workspace recordings" placement="right">
                        <span>{button}</span>
                      </Tooltip>
                    );
                  })}
                </ul>
              </li>
            ))}
            {filteredCategories.length === 0 && (
              <ListItemButton disabled>
                <ListItemText primary="No panels match your search" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
              </ListItemButton>
            )}
          </List>

          {selectedType && selectedDef?.requiresRecording && (
            <FormControl fullWidth size="small">
              <InputLabel>Recording</InputLabel>
              <Select value={selectedRecordingId} label="Recording" onChange={(e) => setSelectedRecordingId(e.target.value)}>
                {recordings.map((rec) => (
                  <MenuItem key={rec.id} value={rec.id}>{rec.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedDef?.requiresSensor && selectedRecordingId && sensorKeys.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Sensor</InputLabel>
              <Select value={selectedSensorKey} label="Sensor" onChange={(e) => setSelectedSensorKey(e.target.value)}>
                {sensorKeys.map((key) => (
                  <MenuItem key={key} value={key}>{friendlyNameConverter(key)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!selectedType || (selectedDef?.requiresRecording && !selectedRecordingId) || (selectedDef?.requiresSensor && !selectedSensorKey)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
